import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const alerts = [
  {
    id: 1,
    type: 'price',
    title: 'BTC Price Alert',
    description: 'Bitcoin crossed above $67,000',
    status: 'triggered',
    timestamp: '10 min ago',
    icon: 'fa-brands fa-bitcoin',
    iconBg: 'bg-crypto-bitcoin/15',
    iconColor: 'text-crypto-bitcoin',
  },
  {
    id: 2,
    type: 'price',
    title: 'ETH Price Alert',
    description: 'Ethereum approaching $3,300 target',
    status: 'watching',
    timestamp: 'Active',
    icon: 'fa-brands fa-ethereum',
    iconBg: 'bg-crypto-ethereum/15',
    iconColor: 'text-crypto-ethereum',
  },
  {
    id: 3,
    type: 'portfolio',
    title: 'Portfolio Alert',
    description: 'Daily profit exceeded $500',
    status: 'triggered',
    timestamp: '2 hours ago',
    icon: 'fa-solid fa-chart-pie',
    iconBg: 'bg-success/10',
    iconColor: 'text-success',
  },
  {
    id: 4,
    type: 'market',
    title: 'Market Alert',
    description: 'High volatility detected in altcoins',
    status: 'warning',
    timestamp: '4 hours ago',
    icon: 'fa-solid fa-triangle-exclamation',
    iconBg: 'bg-warning/10',
    iconColor: 'text-warning',
  },
];

const statusConfig = {
  triggered: {
    label: 'Triggered',
    variant: 'success',
    icon: 'fa-solid fa-check',
  },
  watching: {
    label: 'Watching',
    variant: 'secondary',
    icon: 'fa-solid fa-eye',
  },
  warning: {
    label: 'Warning',
    variant: 'warning',
    icon: 'fa-solid fa-exclamation',
  },
};

const AlertItem = ({ alert }) => {
  const status = statusConfig[alert.status];

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg border border-border bg-card/50 hover:bg-card transition-colors">
      <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0", alert.iconBg)}>
        <i className={cn(alert.icon, alert.iconColor, "text-sm")}></i>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <h4 className="font-medium text-sm truncate">{alert.title}</h4>
          <Badge variant={status.variant} className="text-xs shrink-0">
            <i className={cn(status.icon, "mr-1 text-[10px]")}></i>
            {status.label}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 truncate">{alert.description}</p>
        <p className="text-xs text-muted-foreground mt-1">
          <i className="fa-regular fa-clock mr-1"></i>
          {alert.timestamp}
        </p>
      </div>
    </div>
  );
};

export const AlertsWidget = ({ className, maxItems = 4 }) => {
  const displayedAlerts = alerts.slice(0, maxItems);
  const activeCount = alerts.filter(a => a.status === 'watching').length;
  const triggeredCount = alerts.filter(a => a.status === 'triggered').length;

  return (
    <Card className={cn("h-full", className)} data-testid="alerts-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-warning/10 flex items-center justify-center">
              <i className="fa-solid fa-bell text-warning text-sm"></i>
            </div>
            <CardTitle className="text-base">Price Alerts</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">
              {activeCount} active
            </Badge>
            {triggeredCount > 0 && (
              <Badge variant="success" className="text-xs">
                {triggeredCount} triggered
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {displayedAlerts.map((alert) => (
          <AlertItem key={alert.id} alert={alert} />
        ))}
        <div className="flex gap-2 mt-3">
          <Button variant="outline" className="flex-1" size="sm">
            <i className="fa-solid fa-plus mr-2"></i>
            New Alert
          </Button>
          <Button variant="ghost" size="sm">
            <i className="fa-solid fa-gear"></i>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AlertsWidget;
